import { createContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const AVATAR_CONSENT_SHOWN_KEY = "avatar_consent_shown";

interface PendingAvatarConsent {
  userId: string;
  avatarUrl: string;
  username: string;
}

export interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  rolesLoaded: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  isTeacher: boolean;
  canManageTasks: boolean;
  needsUsername: boolean;
  signOut: () => Promise<void>;
  pendingAvatarConsent: PendingAvatarConsent | null;
  handleAvatarConsent: (accept: boolean) => Promise<void>;
  dismissAvatarConsent: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [needsUsername, setNeedsUsername] = useState(false);
  const [pendingAvatarConsent, setPendingAvatarConsent] = useState<PendingAvatarConsent | null>(null);
  const processedUsersRef = useRef<Set<string>>(new Set());
  const isMountedRef = useRef(true);

  const clearPremiumCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: ["premium_access_role_check"] });
    queryClient.removeQueries({ queryKey: ["premium_pack_task_mappings"] });
    queryClient.removeQueries({ queryKey: ["premium_packs", "purchases"] });
  }, [queryClient]);

  const checkUserRoles = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (error) {
        console.error("Error checking user roles:", error);
        return { isAdmin: false, isModerator: false, isTeacher: false };
      }
      const roles = data?.map(r => r.role) || [];
      return {
        isAdmin: roles.includes("admin"),
        isModerator: roles.includes("moderator"),
        isTeacher: roles.includes("teacher"),
      };
    } catch (err) {
      console.error("Error in checkUserRoles:", err);
      return { isAdmin: false, isModerator: false, isTeacher: false };
    }
  }, []);

  const ensureProfileExists = useCallback(async (authUser: User): Promise<{
    isNewProfile: boolean;
    username: string;
    oauthAvatarUrl: string | null;
    usernameConfirmed: boolean;
  }> => {
    try {
      const { data: existingProfile, error: fetchError } = await supabase
        .from("profiles")
        .select("id, username, avatar_url, username_confirmed")
        .eq("user_id", authUser.id)
        .maybeSingle();

      if (fetchError) {
        console.error("Error checking profile:", fetchError);
        return { isNewProfile: false, username: "", oauthAvatarUrl: null, usernameConfirmed: true };
      }

      if (existingProfile) {
        return {
          isNewProfile: false,
          username: existingProfile.username,
          oauthAvatarUrl: null,
          usernameConfirmed: (existingProfile as any).username_confirmed ?? true,
        };
      }

      // New profile - generate username from metadata or email
      let username = "";
      const userMetadata = authUser.user_metadata;
      if (userMetadata?.username) {
        username = userMetadata.username.substring(0, 30);
      } else if (userMetadata?.full_name) {
        username = userMetadata.full_name.replace(/[^a-zA-Z0-9]/g, "").substring(0, 20);
      } else if (userMetadata?.name) {
        username = userMetadata.name.replace(/[^a-zA-Z0-9]/g, "").substring(0, 20);
      } else if (authUser.email) {
        username = authUser.email.split("@")[0].replace(/[^a-zA-Z0-9]/g, "").substring(0, 20);
      }

      if (username.length < 3) {
        username = `user${Date.now().toString().slice(-6)}`;
      }

      const { data: isAvailable } = await supabase.rpc("check_username_available", { p_username: username });
      if (!isAvailable) {
        username = `${username}${Math.floor(Math.random() * 9999)}`;
      }

      const oauthAvatarUrl = userMetadata?.avatar_url || userMetadata?.picture || null;

      const { error: insertError } = await supabase
        .from("profiles")
        .insert({ user_id: authUser.id, username, avatar_url: null, username_confirmed: false } as any);

      if (insertError) {
        console.error("Error creating profile:", insertError);
        return { isNewProfile: false, username: "", oauthAvatarUrl: null, usernameConfirmed: true };
      }

      return { isNewProfile: true, username, oauthAvatarUrl, usernameConfirmed: false };
    } catch (err) {
      console.error("Error in ensureProfileExists:", err);
      return { isNewProfile: false, username: "", oauthAvatarUrl: null, usernameConfirmed: true };
    }
  }, []);

  const handleAvatarConsent = useCallback(async (accept: boolean) => {
    if (!pendingAvatarConsent) return;

    if (accept) {
      try {
        await supabase
          .from("profiles")
          .update({ avatar_url: pendingAvatarConsent.avatarUrl })
          .eq("user_id", pendingAvatarConsent.userId);
      } catch (err) {
        console.error("Error saving avatar:", err);
      }
    }

    sessionStorage.setItem(AVATAR_CONSENT_SHOWN_KEY, "true");
    setPendingAvatarConsent(null);
  }, [pendingAvatarConsent]);

  const dismissAvatarConsent = useCallback(() => {
    sessionStorage.setItem(AVATAR_CONSENT_SHOWN_KEY, "true");
    setPendingAvatarConsent(null);
  }, []);

  const fetchUserDataAsync = useCallback(async (authUser: User) => {
    if (!isMountedRef.current) return;

    const userId = authUser.id;

    if (!processedUsersRef.current.has(userId)) {
      processedUsersRef.current.add(userId);

      const result = await ensureProfileExists(authUser);

      if (isMountedRef.current) {
        setNeedsUsername(!result.usernameConfirmed);
      }

      if (result.isNewProfile && result.oauthAvatarUrl && isMountedRef.current) {
        const consentShown = sessionStorage.getItem(AVATAR_CONSENT_SHOWN_KEY);
        if (!consentShown) {
          setPendingAvatarConsent({
            userId: authUser.id,
            avatarUrl: result.oauthAvatarUrl,
            username: result.username,
          });
        }
      }
    }

    const rolesResult = await checkUserRoles(userId);
    if (isMountedRef.current) {
      setIsAdmin(rolesResult.isAdmin);
      setIsModerator(rolesResult.isModerator);
      setIsTeacher(rolesResult.isTeacher);
      setRolesLoaded(true);
    }
  }, [ensureProfileExists, checkUserRoles]);

  useEffect(() => {
    isMountedRef.current = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMountedRef.current) return;

        clearPremiumCache();

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          setRolesLoaded(false);
          fetchUserDataAsync(session.user);
        } else {
          setIsAdmin(false);
          setIsModerator(false);
          setIsTeacher(false);
          setRolesLoaded(true);
          setNeedsUsername(false);
        }
      }
    );

    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        if (!isMountedRef.current) return;

        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        setIsLoading(false);

        if (initialSession?.user) {
          fetchUserDataAsync(initialSession.user);
        } else {
          setRolesLoaded(true);
        }
      } catch (error) {
        console.error("Error during initial auth setup:", error);
        if (isMountedRef.current) setIsLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [ensureProfileExists, checkUserRoles, fetchUserDataAsync, clearPremiumCache]);

  const signOut = async () => {
    processedUsersRef.current.clear();
    sessionStorage.removeItem(AVATAR_CONSENT_SHOWN_KEY);
    clearPremiumCache();
    await supabase.auth.signOut({ scope: 'local' });
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsModerator(false);
    setIsTeacher(false);
    setRolesLoaded(true);
    setNeedsUsername(false);
  };

  const canManageTasks = isAdmin || isModerator;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        rolesLoaded,
        isAdmin,
        isModerator,
        isTeacher,
        canManageTasks,
        needsUsername,
        signOut,
        pendingAvatarConsent,
        handleAvatarConsent,
        dismissAvatarConsent,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
